import React, { useContext, useEffect, useMemo, useState } from 'react';
import envConfig from '@nile/home/src/Config/env';
import {
  Loader,
  getTranslatedText,
  withErrorBoundary,
  isAuthorised,
  getCurrentUserHook,
  useIsAuthorisedHook
} from '@nile/components/src';
import RenderChart from '@nile/reports/src/modules/reports/components/subpages/RenderChart';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, isEmpty } from 'lodash';
import { SizeMe } from 'react-sizeme';
import styled from 'styled-components';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import { getEntityIcon } from '@nile/reports/src/modules/charts/utils/chart-transformation/ChartUtil';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { GET_CHART } from '@nile/reports/src/modules/charts/graphql';
import { AccountClient } from '@nile/graphql/src';
import { Button, Modal, TextStyle, Tippy } from '@superops/ui-design-system';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import { WidgetWrapper, Header, Title, Actions } from '../HomeWidgetContainer';
import AccessDenied from './AccessDenied';
import { ChartContextProvider } from '@nile/reports/src/modules/charts/contexts/Chart';
import DataTable from '@nile/reports/src/modules/charts/components/subpages/DataTable';
import { BLACKLISTED_ENTITIES } from '@nile/reports/src/modules/charts/constants';

const Wrapper = styled.div`
  height: calc(100% - 80px);
  padding: 0px 20px;
`;

const TippyWrapper = styled.div`
  font-size: 14px;
  font-weight: 500;
  border: 2px solid #e7e7e7;
  border-radius: 5px;
  box-shadow: 0px 5px 20px #efefef;
  background: white;
  padding: 10px;
`;

const EntityBadge = styled.div.attrs({ className: 'flex actr jctr ptr' })`
  width: 33px;
  min-width: 33px;
  height: 33px;
  border-radius: 50%;
  background-color: ${(props) => (props.color ? props.color : '#fce2e0')};
  .emoji {
    margin: auto;
  }
`;
const NodataWrapper = styled.div`
  height: calc(100% - 80px);
`;

function ReportViewWidget(props) {
  const { deleteWidget, queryVariables, isReadOnly = false } = props;

  const context = useContext(HomeContext);
  const intl = useIntl();

  const [widget, setWidget] = useState({});
  const [selectedChart, setSelectedChart] = useState({
    data: null,
    index: 0,
    originalData: null
  });

  const [getUpdatedWidget, { loading }] = useLazyQuery(GET_CHART, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted: ({ getChart }) => {
      setWidget({ ...props.widget, ...getChart });
    }
  });

  const isReportViewAuthorised = useIsAuthorisedHook('report.view');

  const hasViewPermission = useMemo(() => {
    return props.widget?.permission ?? isReportViewAuthorised;
  }, [isReportViewAuthorised, props.widget?.permission]);

  const navigateToView = () => {
    const currentAppState = context?.userAppState || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState.appState);
    const tabIdx = updatedAppState.findIndex(
      (el) => el.id === 'reports-charts'
    );
    const tab = {
      id: 'reports-charts',
      content: getTabTemplate({
        module: 'REPORTS_CHARTS_HOME',
        data: {
          title: getTranslatedText(intl, 'reports.dashboard.title')
        }
      }),
      accessibilityLabel: 'Client tab',
      module: 'REPORTS_CHARTS_HOME',
      tabId: 11,
      state: {
        activeView: widget?.systemChart ? 'default' : 'custom',
        widget: widget
      },
      data: {},
      url: '/charts/2001/All Charts'
    };

    if (tabIdx === -1) {
      updatedAppState.push(tab);
    } else {
      updatedAppState[tabIdx] = tab;
    }
    context.updateUserAppState({
      ...currentAppState,
      appState: updatedAppState,
      activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
    });
    context.closeHomeOverlay();
    setTimeout(() => {
      context.switchTabFocus(tab);
    }, 200);
  };

  const formattedQueryVariable = useMemo(() => {
    if (queryVariables) {
      const { operands, reportCondition } = preFormatFilterObject({
        entity: widget.entity,
        filterObj: queryVariables.condition.operands
      });
      return {
        chartId: widget.chartId,
        condition:
          (reportCondition &&
            !isEmpty(operands) && {
              joinOperator: 'AND',
              operands
            }) ||
          null
      };
    }

    return null;
  }, [queryVariables, widget]);

  /**
   * Method to append the metric filterCondition with the parent filterCondition
   * @returns widget
   */
  const constructDataTableChartProps = (chartData) => {
    let updatedChart = chartData;
    if (queryVariables) {
      const { operands, reportCondition } = preFormatFilterObject({
        entity: widget.entity,
        filterObj: queryVariables.condition.operands,
        removeLabel: true
      });
      const condition =
        (reportCondition &&
          !isEmpty(operands) && { joinOperator: 'AND', operands }) ||
        null;
      // Append FilterConditions of the widget to the existing filter condition
      if (condition != null) {
        updatedChart = cloneDeep(chartData);
        updatedChart.data?.metrics?.forEach((metric) => {
          if (metric.filterCondition?.operands?.length > 0)
            metric.filterCondition.operands.push(condition);
          else {
            metric.filterCondition = {
              operands: [...condition.operands],
              joinOperator: condition?.joinOperator
            };
          }
        });
      }
    }
    setSelectedChart(updatedChart);
  };

  useEffect(() => {
    if (!hasViewPermission) return;

    getUpdatedWidget({
      variables: {
        chartId: props.widget?.chartId
      }
    });
  }, []);

  /**
   * Method to check if data table is visible
   * @param {*} chart
   * @returns boolean
   */
  const isDataTableHidden = (chart) => {
    return (
      chart && !chart.chartId && BLACKLISTED_ENTITIES.includes(chart.entity)
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!hasViewPermission) {
    return (
      <SizeMe monitorHeight monitorWidth resizeDetectorStrategy="object">
        {({ size: childSize }) => (
          <WidgetWrapper>
            <Header>
              <div className="fx actr">
                <EntityBadge
                  color={getEntityIcon(widget?.entity).badgeColor}
                  className="mr10"
                >
                  {getEntityIcon(widget?.entity).icon}
                </EntityBadge>
                <div className="ellipsis reportName chart-title">
                  {<Title>{widget?.name}</Title> || (
                    <TextStyle variation="subdued">
                      <FormattedMessage id="app.noData.message" />
                    </TextStyle>
                  )}
                </div>
              </div>
              <div className="widget-header flex center fg1">
                <Actions className="widget-actions visibilityActions ptr mr8">
                  <Button
                    plain
                    onClick={deleteWidget.bind(this, props.widget)}
                    icon={<SVGIcon name="Delete" />}
                  />
                </Actions>
              </div>
            </Header>

            <Wrapper>
              <AccessDenied small={childSize.height < 200} />
            </Wrapper>
          </WidgetWrapper>
        )}
      </SizeMe>
    );
  }

  return (
    <SizeMe monitorHeight monitorWidth resizeDetectorStrategy="object">
      {({ size: childSize }) => (
        <WidgetWrapper>
          <Header isReadOnly={isReadOnly}>
            {isEmpty(widget?.description?.trim()) ? (
              <div
                className="fx actr"
                onClick={() => {
                  if (!isReadOnly) {
                    navigateToView();
                  }
                }}
              >
                <EntityBadge
                  color={getEntityIcon(widget?.entity).badgeColor}
                  className="mr10"
                >
                  {getEntityIcon(widget?.entity).icon}
                </EntityBadge>
                <div className="ellipsis reportName chart-title">
                  {<Title isReadOnly={isReadOnly}>{widget?.name}</Title> || (
                    <TextStyle variation="subdued">
                      <FormattedMessage id="app.noData.message" />
                    </TextStyle>
                  )}
                </div>
              </div>
            ) : (
              <Tippy
                html={<TippyWrapper>{widget?.description}</TippyWrapper>}
                allowHTML
                theme="light"
                position="bottom-start"
                className="fximp"
              >
                <div
                  className="fx actr"
                  onClick={() => {
                    if (!isReadOnly) {
                      navigateToView();
                    }
                  }}
                >
                  <EntityBadge
                    color={getEntityIcon(widget?.entity).badgeColor}
                    className="mr10"
                  >
                    {getEntityIcon(widget?.entity).icon}
                  </EntityBadge>
                  <div className="ellipsis reportName chart-title">
                    {<Title isReadOnly={isReadOnly}>{widget?.name}</Title> || (
                      <TextStyle variation="subdued">
                        <FormattedMessage id="app.noData.message" />
                      </TextStyle>
                    )}
                  </div>
                  <div className="fx actr jctr mlr10">
                    <SVGIcon name="MenuBurgerSquare" />
                  </div>
                </div>
              </Tippy>
            )}
            {!isReadOnly && (
              <div className="widget-header flex center fg1">
                <Actions className="widget-actions visibilityActions ptr mr8">
                  <Button
                    plain
                    onClick={deleteWidget.bind(this, widget)}
                    icon={<SVGIcon name="Delete" />}
                  />
                </Actions>
              </div>
            )}
          </Header>
          {!isEmpty(widget) ? (
            <>
              <Wrapper>
                <RenderChart
                  item={{ chart: widget }}
                  size={{
                    width: childSize.width - 40,
                    height: childSize.height - 100
                  }}
                  queryVariables={formattedQueryVariable}
                  numberChartCallback={(chartData) => {
                    constructDataTableChartProps(chartData);
                  }}
                  numberChartTooltip={getTranslatedText(
                    intl,
                    'common.view.data'
                  )}
                />
              </Wrapper>
              {/* Data table view of the chart */}
              {selectedChart.data != null && !isDataTableHidden(selectedChart) && (
                <Modal
                  large
                  open
                  onClose={() => setSelectedChart({ data: null, index: 0 })}
                >
                  <ChartContextProvider
                    value={{
                      value: selectedChart.data,
                      isEdit: true,
                      metricIndex: selectedChart?.index
                    }}
                  >
                    <DataTable
                      showExportButton={selectedChart?.data?.chartId}
                      tableHeader={selectedChart?.data?.name}
                      defaultCondition={
                        selectedChart?.data?.metrics?.[
                          selectedChart?.index || 0
                        ]?.filterCondition
                      }
                      isTableOpen={true}
                      isTableEditable={false}
                      disableToggle={true}
                      isFromWidgetView
                    />
                  </ChartContextProvider>
                </Modal>
              )}
            </>
          ) : (
            <NodataWrapper className="fx actr jctr">
              <img
                src={`${envConfig.imageRoot}svg/not-found.svg`}
                alt="No data"
              />
            </NodataWrapper>
          )}
        </WidgetWrapper>
      )}
    </SizeMe>
  );
}

export default withErrorBoundary(ReportViewWidget);
